import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit2, Trash2, Loader2, AlertCircle, CheckCircle2, Clock, Rocket, EyeOff, Upload } from 'lucide-react';
import ContractGatePrompt from '../components/ContractGatePrompt';
import GuildProjectLinkCard from '../components/GuildProjectLinkCard';
import TheatricalListingCard from '../components/TheatricalListingCard';
import CollaboratorSplitCard from '../components/CollaboratorSplitCard';
import {
  CONTRACT_GATE_MESSAGE,
  requiresContractForPublishing,
} from '../lib/contractGate';

interface Work {
  id: string;
  title: string;
  description: string | null;
  status: 'draft' | 'published' | 'archived';
  content_page_state: string | null;
  poster_url: string | null;
  published_at: string | null;
}

interface Season {
  id: string;
  work_id: string;
  season_number: number;
  title: string | null;
}

interface Episode {
  id: string;
  season_id: string;
  episode_number: number;
  title: string;
  description: string | null;
  duration_seconds: number | null;
}

interface EpisodeVideoStatus {
  episode_id: string;
  has_ingested_asset: boolean;
  mux_ready: boolean;
}

interface EpisodeForm {
  title: string;
  description: string;
  duration_minutes: string;
}

const defaultForm: EpisodeForm = { title: '', description: '', duration_minutes: '' };

export default function SeriesDetail() {
  const { seriesId } = useParams<{ seriesId: string }>();
  const { publisher } = useAuth();
  const navigate = useNavigate();

  const [work, setWork] = useState<Work | null>(null);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);

  // Episode video readiness (for publish checks)
  const [episodeVideoStatuses, setEpisodeVideoStatuses] = useState<EpisodeVideoStatus[]>([]);

  // Add/Edit episode modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState<Episode | null>(null);
  const [targetSeasonId, setTargetSeasonId] = useState<string>('');
  const [form, setForm] = useState<EpisodeForm>(defaultForm);
  const [saving, setSaving] = useState(false);

  // Add season
  const [addingSeasonLoading, setAddingSeasonLoading] = useState(false);

  // Delete confirm
  const [deleteEpisodeId, setDeleteEpisodeId] = useState<string | null>(null);

  // Publish / unpublish
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [unpublishConfirmOpen, setUnpublishConfirmOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    if (seriesId && publisher?.id) fetchAll();
  }, [seriesId, publisher?.id]);

  const fetchAll = async () => {
    if (!seriesId || !publisher?.id) return;
    setLoading(true);
    try {
      const { data: workData, error: workError } = await supabase
        .from('works')
        .select('id, title, description, status, content_page_state, poster_url, published_at')
        .eq('id', seriesId)
        .eq('publisher_id', publisher.id)
        .single();

      if (workError) throw workError;
      setWork(workData as unknown as Work);

      const { data: seasonData } = await supabase
        .from('seasons')
        .select('id, work_id, season_number, title')
        .eq('work_id', seriesId)
        .order('season_number');
      const fetchedSeasons = seasonData || [];
      setSeasons(fetchedSeasons);

      let fetchedEpisodes: Episode[] = [];
      if (fetchedSeasons.length > 0) {
        const seasonIds = fetchedSeasons.map((s: Season) => s.id);
        const { data: epData } = await supabase
          .from('episodes_new')
          .select('id, season_id, episode_number, title, description, duration_seconds')
          .in('season_id', seasonIds)
          .order('season_id')
          .order('episode_number');
        fetchedEpisodes = epData || [];
        setEpisodes(fetchedEpisodes);
      } else {
        setEpisodes([]);
      }

      // Fetch video readiness for all episodes
      if (fetchedEpisodes.length > 0) {
        const episodeIds = fetchedEpisodes.map(e => e.id);

        // Check which episodes have ingested assets
        const { data: assetData } = await supabase
          .from('pending_uploads')
          .select('episode_id, upload_status')
          .in('episode_id', episodeIds)
          .eq('upload_status', 'ingested');

        // Check which episodes have mux-ready videos
        const { data: videoData } = await supabase
          .from('videos')
          .select('parent_id, mux_status')
          .in('parent_id', episodeIds)
          .eq('parent_type', 'episode')
          .eq('mux_status', 'ready');

        const ingestedEpIds = new Set((assetData || []).map((a: any) => a.episode_id));
        const muxReadyEpIds = new Set((videoData || []).map((v: any) => v.parent_id));

        setEpisodeVideoStatuses(
          fetchedEpisodes.map(ep => ({
            episode_id: ep.id,
            has_ingested_asset: ingestedEpIds.has(ep.id),
            mux_ready: muxReadyEpIds.has(ep.id),
          }))
        );
      } else {
        setEpisodeVideoStatuses([]);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to load series');
    } finally {
      setLoading(false);
    }
  };

  // ── Publish readiness ──────────────────────────────────────────────
  const canPublish = (): { ok: boolean; reason?: string; readyCount?: number } => {
    if (episodes.length === 0) {
      return { ok: false, reason: 'No episodes added yet' };
    }

    const readyEpisodes = episodeVideoStatuses.filter(
      s => s.has_ingested_asset && s.mux_ready
    );

    if (readyEpisodes.length === 0) {
      return { ok: false, reason: 'No episodes have a processed, playback-ready video' };
    }

    return { ok: true, readyCount: readyEpisodes.length };
  };

  const contractBlocked = requiresContractForPublishing(publisher);

  const handlePublish = async () => {
    if (!work) return;
    if (contractBlocked) {
      toast.error(CONTRACT_GATE_MESSAGE);
      return;
    }
    setPublishing(true);
    try {
      const { error } = await supabase.from('works').update({
        status: 'published',
        content_page_state: 'released',
        published_at: new Date().toISOString(),
      }).eq('id', work.id);

      if (error) throw error;

      setWork(prev => prev ? {
        ...prev,
        status: 'published',
        content_page_state: 'released',
        published_at: new Date().toISOString(),
      } : prev);
      toast.success('Series published!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to publish');
    } finally {
      setPublishing(false);
      setPublishConfirmOpen(false);
    }
  };

  const handleUnpublish = async () => {
    if (!work) return;
    setPublishing(true);
    try {
      const { error } = await supabase.from('works').update({
        status: 'draft',
      }).eq('id', work.id);

      if (error) throw error;

      setWork(prev => prev ? { ...prev, status: 'draft' } : prev);
      toast.success('Series unpublished');
    } catch (err: any) {
      toast.error(err.message || 'Failed to unpublish');
    } finally {
      setPublishing(false);
      setUnpublishConfirmOpen(false);
    }
  };

  // ── Season / episode CRUD ──────────────────────────────────────────
  const handleAddSeason = async () => {
    if (!seriesId) return;
    setAddingSeasonLoading(true);
    try {
      const nextNumber = seasons.length > 0 ? Math.max(...seasons.map(s => s.season_number)) + 1 : 1;
      const { data, error } = await supabase
        .from('seasons')
        .insert({ work_id: seriesId, season_number: nextNumber })
        .select()
        .single();
      if (error) throw error;
      setSeasons(prev => [...prev, data]);
      toast.success(`Season ${nextNumber} added`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to add season');
    } finally {
      setAddingSeasonLoading(false);
    }
  };

  const openAddEpisode = (seasonId: string) => {
    const seasonEps = episodes.filter(e => e.season_id === seasonId);
    const nextEp = seasonEps.length > 0 ? Math.max(...seasonEps.map(e => e.episode_number)) + 1 : 1;
    setEditingEpisode(null);
    setTargetSeasonId(seasonId);
    setForm({ ...defaultForm, duration_minutes: '' });
    // Pre-fill episode number as a hint (not stored in form but shown)
    setForm(f => ({ ...f, _nextEp: nextEp } as EpisodeForm & { _nextEp?: number }));
    setModalOpen(true);
  };

  const openEditEpisode = (ep: Episode) => {
    setEditingEpisode(ep);
    setTargetSeasonId(ep.season_id);
    setForm({
      title: ep.title,
      description: ep.description ?? '',
      duration_minutes: ep.duration_seconds ? String(Math.round(ep.duration_seconds / 60)) : '',
    });
    setModalOpen(true);
  };

  const handleSaveEpisode = async () => {
    if (!form.title.trim()) { toast.error('Episode title is required'); return; }
    const durationSec = form.duration_minutes ? parseInt(form.duration_minutes) * 60 : null;

    setSaving(true);
    try {
      if (editingEpisode) {
        const { error } = await supabase
          .from('episodes_new')
          .update({ title: form.title.trim(), description: form.description.trim() || null, duration_seconds: durationSec })
          .eq('id', editingEpisode.id);
        if (error) throw error;
        toast.success('Episode updated');
      } else {
        const seasonEps = episodes.filter(e => e.season_id === targetSeasonId);
        const nextEp = seasonEps.length > 0 ? Math.max(...seasonEps.map(e => e.episode_number)) + 1 : 1;
        const { error } = await supabase.from('episodes_new').insert({
          season_id: targetSeasonId,
          episode_number: nextEp,
          title: form.title.trim(),
          description: form.description.trim() || null,
          duration_seconds: durationSec,
        });
        if (error) throw error;
        toast.success('Episode added');
      }
      setModalOpen(false);
      setForm(defaultForm);
      await fetchAll();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save episode');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEpisode = async () => {
    if (!deleteEpisodeId) return;
    try {
      const { error } = await supabase.from('episodes_new').delete().eq('id', deleteEpisodeId);
      if (error) throw error;
      setEpisodes(prev => prev.filter(e => e.id !== deleteEpisodeId));
      toast.success('Episode deleted');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete episode');
    } finally {
      setDeleteEpisodeId(null);
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '—';
    const m = Math.floor(seconds / 60);
    return `${m} min`;
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading series...</div>;
  }

  if (!work) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Series not found.</p>
        <Button className="mt-4" onClick={() => navigate('/content/series')}>Back to Series</Button>
      </div>
    );
  }

  const publishCheck = canPublish();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/content/series')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold truncate">{work.title}</h1>
            <Badge
              variant={work.status === 'published' ? 'default' : 'outline'}
              className={`capitalize ${work.status === 'published' ? 'bg-green-600/20 text-green-400 border-green-500/30' : ''}`}
            >
              {work.status}
            </Badge>
          </div>
          {work.description && (
            <p className="text-muted-foreground mt-1 text-sm">{work.description}</p>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddSeason}
          disabled={addingSeasonLoading}
        >
          {addingSeasonLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
          Add Season
        </Button>
      </div>

      {/* Seasons */}
      {seasons.length === 0 ? (
        <Card className="partner-card">
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground">No seasons yet.</p>
            <Button className="mt-4" onClick={handleAddSeason} disabled={addingSeasonLoading}>
              <Plus className="h-4 w-4 mr-1" />
              Add Season 1
            </Button>
          </CardContent>
        </Card>
      ) : (
        seasons.map(season => {
          const seasonEps = episodes.filter(e => e.season_id === season.id);
          return (
            <Card key={season.id} className="partner-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Season {season.season_number}</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => openAddEpisode(season.id)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Episode
                  </Button>
                </div>
                <CardDescription>
                  {seasonEps.length} episode{seasonEps.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {seasonEps.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No episodes yet. Add the first episode.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {seasonEps.map(ep => {
                      const videoStatus = episodeVideoStatuses.find(s => s.episode_id === ep.id);
                      const isReady = videoStatus?.has_ingested_asset && videoStatus?.mux_ready;
                      return (
                        <div
                          key={ep.id}
                          className="flex items-center gap-3 p-3 rounded-lg border border-border/40 bg-muted/20"
                        >
                          <span className="text-sm font-mono text-muted-foreground w-8 shrink-0">
                            E{ep.episode_number}
                          </span>
                          <span className="flex-1 text-sm font-medium truncate">{ep.title}</span>
                          {videoStatus && (
                            <span className="shrink-0" title={isReady ? 'Video ready' : 'Video not ready'}>
                              {isReady
                                ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                                : <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                              }
                            </span>
                          )}
                          <span className="text-sm text-muted-foreground shrink-0">
                            {formatDuration(ep.duration_seconds)}
                          </span>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              title="Upload video for this episode"
                              onClick={() => navigate(`/content/new?work_id=${seriesId}&episode_id=${ep.id}`)}
                            >
                              <Upload className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => openEditEpisode(ep)}
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive hover:text-destructive"
                              onClick={() => setDeleteEpisodeId(ep.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })
      )}

      {/* ── Guild project link ─────────────────────────────────────── */}
      {work && <GuildProjectLinkCard workId={work.id} />}

      {/* ── Theatrical marketplace listing ─────────────────────────── */}
      {work && <TheatricalListingCard workId={work.id} />}

      {work && <CollaboratorSplitCard workId={work.id} />}

      {/* ── Publishing section ─────────────────────────────────────── */}
      <Card className={`partner-card ${
        work.status === 'published'
          ? 'border-green-500/20'
          : publishCheck.ok
            ? 'border-publisher-500/20'
            : ''
      }`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Publishing</CardTitle>
        </CardHeader>
        <CardContent>
          {work.status === 'draft' && publishCheck.ok && contractBlocked && (
            <ContractGatePrompt />
          )}

          {work.status === 'draft' && publishCheck.ok && !contractBlocked && (
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-500/10">
                  <Rocket className="h-4 w-4 text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium">Ready to publish</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {publishCheck.readyCount} episode{publishCheck.readyCount !== 1 ? 's' : ''} with playback-ready video
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setPublishConfirmOpen(true)}
                className="shrink-0 bg-green-600 hover:bg-green-700 text-white"
              >
                <Rocket className="h-4 w-4 mr-1.5" />
                Publish Now
              </Button>
            </div>
          )}

          {work.status === 'draft' && !publishCheck.ok && (
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-yellow-500/10">
                <AlertCircle className="h-4 w-4 text-yellow-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Cannot publish yet</p>
                <p className="text-xs text-muted-foreground mt-0.5">{publishCheck.reason}</p>
              </div>
            </div>
          )}

          {work.status === 'published' && (
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-500/10">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-green-400">Published</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Visible to all subscribers
                    {work.published_at && (
                      <> &middot; since {new Date(work.published_at).toLocaleDateString()}</>
                    )}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setUnpublishConfirmOpen(true)}
                className="shrink-0"
              >
                <EyeOff className="h-4 w-4 mr-1.5" />
                Unpublish
              </Button>
            </div>
          )}

          {work.status === 'archived' && (
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                <Clock className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Archived</p>
                <p className="text-xs text-muted-foreground mt-0.5">This series is archived and not visible to viewers</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Publish confirmation dialog ────────────────────────────── */}
      <AlertDialog open={publishConfirmOpen} onOpenChange={setPublishConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish &ldquo;{work.title}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will make your series visible to all Kinora subscribers immediately.
              {publishCheck.readyCount && publishCheck.readyCount < episodes.length && (
                <> Note: only {publishCheck.readyCount} of {episodes.length} episodes have playback-ready video.</>
              )}
              {' '}You can unpublish at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePublish}
              disabled={publishing}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {publishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Rocket className="h-4 w-4 mr-1.5" />}
              Publish Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Unpublish confirmation dialog ──────────────────────────── */}
      <AlertDialog open={unpublishConfirmOpen} onOpenChange={setUnpublishConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unpublish &ldquo;{work.title}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove your series from the public catalog. Subscribers will no longer
              be able to find or watch it. You can re-publish at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnpublish}
              disabled={publishing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {publishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <EyeOff className="h-4 w-4 mr-1.5" />}
              Unpublish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add/Edit Episode Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEpisode ? 'Edit Episode' : 'Add Episode'}</DialogTitle>
            <DialogDescription>
              {editingEpisode ? 'Update episode details.' : 'Add a new episode to this season.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Episode Title *</Label>
              <Input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Episode title"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Episode description"
                rows={3}
                className="resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label>Duration (minutes)</Label>
              <Input
                type="number"
                min="1"
                max="60"
                value={form.duration_minutes}
                onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))}
                placeholder="45"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEpisode} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingEpisode ? 'Save Changes' : 'Add Episode'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteEpisodeId} onOpenChange={open => !open && setDeleteEpisodeId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Episode?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the episode. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteEpisode}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
